/**
 * Firebase Configuration with Offline Persistence
 * SECURITY: All sensitive credentials are stored in environment variables
 * PERFORMANCE: Enabled persistent local cache for instant offline access
 */

import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { 
  getFirestore, 
  persistentLocalCache,
  persistentMultipleTabManager,
  initializeFirestore,
  CACHE_SIZE_UNLIMITED
} from 'firebase/firestore';
import { getAnalytics } from 'firebase/analytics';

/**
 * Validate that all required environment variables are present
 */
const validateConfig = () => {
  const required = [
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_FIREBASE_APP_ID'
  ];
  
  const missing = required.filter(key => !process.env[key]);
  
  if (missing.length > 0) {
    console.error('Firebase Configuration Missing:', missing);
    return false;
  }
  
  console.log('✓ Firebase configuration loaded');
  return true;
};

const isConfigValid = validateConfig();

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

let app;
let auth;
let db;
let analytics;

if (isConfigValid) {
  try {
    // Initialize Firebase App
    app = initializeApp(firebaseConfig);
    
    // Initialize Auth
    auth = getAuth(app);
    
    // Initialize Firestore with Persistent Cache
    // This enables offline support and instant data loading from IndexedDB
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
        cacheSizeBytes: CACHE_SIZE_UNLIMITED
      })
    });
    
    console.log('✓ Firestore initialized with persistent cache');
    
    // Initialize Analytics (production only)
    if (process.env.NODE_ENV === 'production' && firebaseConfig.measurementId) {
      analytics = getAnalytics(app);
    }
    
  } catch (error) {
    console.error('Error initializing Firebase:', error);
    
    // Fallback: Try without persistence if it fails
    if (!db) {
      try {
        db = getFirestore(app);
        console.warn('⚠ Firestore initialized without persistence');
      } catch (fallbackError) {
        console.error('Fatal: Could not initialize Firestore', fallbackError);
      }
    }
  }
}

export { auth, db, analytics };
export default app;
